import { useCallback } from 'react';
import { useIntl } from 'react-intl';

/*
 * Generic hook for use in apps that define a mapping of English phrases
 * to their corresponding translation key, like so:
 *
 *     const keys = {
 *       "Cancel": "cancel",
 *       "Save": "save",
 *       "Welcome to Material Bank", "welcome-to-material-bank",
 *     }
 *
 * Each app should export its own custom hook...
 *
 *     export const useTranslate = () => useGenericTranslate(keys)
 *
 * ...to be used in the app's components, like this:
 *
 *     function CancelButton() {
 *       const t = useTranslate()
 *       return <button>{t('Cancel')}</button>
 *     }
 */
export function useGenericTranslate<EnglishPhrase extends string>(
  keys: Record<EnglishPhrase, string>,
) {
  const intl = useIntl();

  const translate = useCallback(
    (englishPhrase: EnglishPhrase) => {
      return intl.formatMessage({
        id: keys[englishPhrase] ?? '-----',
        defaultMessage: englishPhrase,
      });
    },
    [intl, keys],
  );

  return translate;
}
